import React, { useCallback, useEffect, useState } from "react";
import classNames from "classnames";
import { Feature } from "ol";
import { AccordionItem } from "../../Elements/Accordion";
import { executeQuery } from "../../Map/rest";
import { useAppContext } from "../../Contexts/AppContext";
import { config } from "../../Map/config";
import { useDrawingContext } from "../../Contexts/DrawingContext";
import { DrawType } from "../../Map/enums";
import { SearchInfoButton } from "../../Elements/SearchInfoButton";
import { useRegistrationContext } from "../../Contexts/RegistrationContext";
import { SearchPopup } from "../../Popups/SearchPopup";

const maxResults = 5;
const searchConfig = config.search;
export const searchTitle = "Meklēt īpašumu, ceļu vai pagastu";

export const Search = (): JSX.Element => {
    const [text, setText] = useState<string>("");
    const [results, setResults] = useState<Feature[]>([]);
    const [loading, setLoading] = useState<boolean>(false);
    const [selected, setSelected] = useState<Feature>();

    const { setLocateFeature } = useAppContext();
    const { drawnFeatures, searchInfoVisible } = useRegistrationContext();
    const { addFromSearch } = useDrawingContext();

    useEffect(() => {
        setLocateFeature(selected);
    }, [selected]);

    const executeSearch = useCallback(async () => {
        if (!text) {
            return;
        }
        setLoading(true);
        const queries = buildQueries(text);
        const queryResults = await doQuery(queries);
        setResults(queryResults.slice(0, maxResults));
        setLoading(false);
    }, [text]);

    const onChange = (e) => {
        setResults([]);
        setSelected(undefined);
        setText(e.target.value);
    };

    const onSelect = (selectedFeature: Feature) => {
        setSelected(selectedFeature);
        setText(getValue(selectedFeature));
    };

    const onClear = () => {
        setText("");
        setSelected(undefined);
        setResults([]);
    };

    const onKeyDown = (e) => {
        e.key === "Enter" && executeSearch();
    };

    const onAddSelected = (e) => {
        e.stopPropagation();
        addFromSearch(createFeature());
        onClear();
    };

    const createFeature = () => {
        const newFeature = new Feature();
        newFeature.setGeometry(selected.getGeometry());
        newFeature.setProperties({
            TYPE: 1,
            DRAWTYPE: selected.get("DRAWTYPE"),
            MASTERID: Number(selected.get("OBJECTID"))
        });

        const cadaster = selected.get("CODE");
        cadaster && newFeature.set("CADASTER", cadaster);

        const roadName = selected.get("ROADNAME");
        roadName && newFeature.set("MASTERNAME", roadName);

        return newFeature;
    };

    return (
        <>
            <AccordionItem title={searchTitle} defaultExpanded={Date.now()} changeHeight={Date.now()} key={searchTitle}>
                <div className="draw-toolbar search-bar">
                    <div className="symbol-label" onClick={executeSearch}>
                        <span className="icon-geo-search"></span>
                    </div>
                    <div className="search-input">
                        <input
                            type="text"
                            placeholder="Meklēt "
                            value={text}
                            onChange={onChange}
                            onKeyDown={onKeyDown}
                        />
                        {loading && (
                            <div className="search-clear">
                                <span className="icon-geo-spinner rotating"></span>
                            </div>
                        )}
                        {!loading && text && (
                            <div className="search-clear" onClick={onClear}>
                                <span className="icon-geo-close-popup"></span>
                            </div>
                        )}
                        <div className={classNames("search-list", { none: selected })}>
                            {results.map((result, i) => (
                                <div className="list-option" key={"option-" + i} onClick={() => onSelect(result)}>
                                    {getValue(result)}
                                </div>
                            ))}
                        </div>
                    </div>
                    <SearchInfoButton></SearchInfoButton>
                </div>
                {selected && (
                    <div className="search-toolbar" onClick={() => setLocateFeature(selected)}>
                        <div className="search-toolbar-button">
                            <span className="icon-geo-zoom-in"></span>
                        </div>
                        {selected.get("ROADNAME") ||
                        (selected.get("CODE") &&
                            !drawnFeatures.find((f) => f.get("DRAWTYPE") === DrawType.PolygonProperty)) ? (
                            <div className="search-toolbar-button" onClick={onAddSelected}>
                                <span className="icon-geo-plus"></span>
                            </div>
                        ) : null}
                        <div className="search-toolbar-text">{getValue(selected)}</div>
                    </div>
                )}
            </AccordionItem>
            {searchInfoVisible ? <SearchPopup></SearchPopup> : null}
        </>
    );
};

const buildQueries = (searchText: string) =>
    searchConfig.map((searchLayer) => ({
        layerId: searchLayer.layerId,
        url: searchLayer.url,
        field: searchLayer.field,
        params: {
            f: "json",
            where: "UPPER(" + searchLayer.field + ") LIKE '%" + searchText.toUpperCase() + "%'",
            outFields: "*"
        }
    }));

const doQuery = async (queries) => {
    const results = [];
    for (const query of queries) {
        const features = await executeQuery(query.params, query.url, query.layerId);
        features.length && query.field === "ROADNAME"
            ? features.map((f) => f.set("DRAWTYPE", DrawType.PolylineLvmRoad))
            : query.field === "CODE"
            ? features.map((f) => f.set("DRAWTYPE", DrawType.PolygonProperty))
            : false;

        results.push(...features);
    }
    return results;
};

const getValue = (feature: Feature) => {
    let value;
    for (const searchLayer of config.search) {
        value = feature.get(searchLayer.field);
        if (value) {
            break;
        }
    }
    return value;
};
